<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarLocalLoader, GrammarModal, GrammarText } from '@nora-monorepo/grammar-ui';
import {
  AvatarImage,
  getInitials,
  HoverableUserName,
} from '@nora-monorepo/ui-shared';

import { useSeenByModalStore } from '#nora-chat-ui/stores/conversation/message/seen-by-modal-store';

const emit = defineEmits(['close']);

const { d, t } = useI18n();

const seenByModalStore = useSeenByModalStore();
const { seenByUsers, isLoading, hasError, isOpen } = storeToRefs(seenByModalStore);

const seenByModalTitle = computed(() => {
  if (hasError.value) {
    return t('messageActions.seenBy');
  }

  if (seenByUsers.value.length === 1) {
    return t('seenByModal.titleSingle');
  }
  return t('seenByModal.title', [seenByUsers.value.length]);
});

function close() {
  emit('close');
}
</script>

<template>
  <GrammarModal :is-open="isOpen" :label="seenByModalTitle" @close="close">
    <template #content>
      <div v-if="isLoading">
        <div
          class="flex justify-content-center align-items-center w-full h-full"
        >
          <GrammarLocalLoader />
        </div>
      </div>
      <div v-else-if="hasError" class="my-2 px-5 py-3 w-[min(80vw,28rem)]">
        <GrammarText :text="t('seenByModal.error')" />
      </div>
      <div v-else class="my-2 w-[min(80vw,28rem)] max-h-[min(60vh,28rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
        <div class="space-y-2">
          <div
            v-for="(seenByUser, index) in seenByUsers"
            :key="`${seenByUser.user.id}-${index}`"
            class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm transition-colors"
          >
            <AvatarImage
              :id="seenByUser.user.id"
              :text="getInitials(seenByUser.user)"
            />
            <div class="flex-1 min-w-0">
              <HoverableUserName :user="seenByUser.user" />
            </div>
            <div
              v-if="seenByUser.messageReadDateTime"
              class="text-sm text-gray-600 dark:text-gray-400"
            >
              <GrammarText
                :text="d(seenByUser.messageReadDateTime, 'long')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </GrammarModal>
</template>
